import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import MoonMap from "../../components/MoonMap/MoonMap";
import { fetchAnomalies, fetchFeatures, fetchStats, fetchTypes } from "../../lib/api";
import { formatEventDate, TYPE_LABELS } from "../../lib/format";
import type { Feature, LunarEvent, PhenomenonType, PhenomenonTypeId, Stats } from "../../types";

const MapView = () => {
  const [events, setEvents] = useState<LunarEvent[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [types, setTypes] = useState<PhenomenonType[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<PhenomenonTypeId | "">("");
  const [selectedId, setSelectedId] = useState<string | null>(
    searchParams.get("feature"),
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchAnomalies({ pageSize: 1000 }),
      fetchFeatures(),
      fetchTypes(),
      fetchStats(),
    ])
      .then(([anomalyPage, featureList, typeList, statsValue]) => {
        if (cancelled) return;
        setEvents(anomalyPage.items);
        setFeatures(featureList);
        setTypes(typeList);
        setStats(statsValue);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredEvents = useMemo(() => {
    const query = q.trim().toLowerCase();
    return events.filter((event) => {
      if (typeFilter && event.phenomenonTypeId !== typeFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const hay = [
        event.description,
        event.locationText,
        event.featureName,
        event.observer,
        event.reference,
        event.dateText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });
  }, [events, q, typeFilter]);

  const visibleFeatures = useMemo(() => {
    const counts = new Map<string, number>();
    for (const event of filteredEvents) {
      if (event.featureId) {
        counts.set(event.featureId, (counts.get(event.featureId) ?? 0) + 1);
      }
    }
    return features
      .filter((feature) => counts.has(feature.id))
      .map((feature) => ({ ...feature, eventCount: counts.get(feature.id) ?? 0 }));
  }, [features, filteredEvents]);

  const selectedEvents = useMemo(() => {
    if (!selectedId) {
      return [];
    }
    return filteredEvents.filter((event) => event.featureId === selectedId);
  }, [filteredEvents, selectedId]);

  const selectedFeature = visibleFeatures.find((feature) => feature.id === selectedId);

  return (
    <Layout fullHeight>
      <div className="relative flex-1 min-h-0 h-full">
        <div className="absolute inset-0 bg-alt-blk">
          {error ? (
            <div className="h-full flex items-center justify-center p-8 text-center text-alt-wht">
              <div>
                <p className="text-xl font-semibold mb-2">Could not load the catalog</p>
                <p className="text-alt-wht/70">{error}</p>
                <p className="mt-4 text-sm">
                  Start the API with{" "}
                  <code className="bg-white/10 px-1 rounded">
                    npm --prefix api start
                  </code>
                </p>
              </div>
            </div>
          ) : (
            <MoonMap
              features={visibleFeatures}
              selectedId={selectedId}
              onSelect={(feature) => setSelectedId(feature.id)}
            />
          )}
        </div>

        <aside className="absolute z-[1100] top-4 left-4 right-4 md:right-auto md:w-[380px] max-h-[calc(100%-2rem)] overflow-y-auto rounded-2xl bg-alt-blk text-alt-wht shadow-2xl p-5 space-y-4">
          <div>
            <h1 className="text-2xl text-ray font-bold">Lunamalies</h1>
            <p className="text-sm text-alt-wht/80 mt-1">
              {stats
                ? `${stats.eventCount.toLocaleString()} reports · ${stats.yearMin}–${stats.yearMax} · ${stats.featureCount} named features`
                : "Loading lunar anomaly catalog…"}
            </p>
          </div>

          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search descriptions, observers, features…"
            className="w-full rounded-xl bg-white/10 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-ray"
          />

          <div className="flex flex-wrap gap-2">
            <button
              className={`text-xs px-3 py-1 rounded-full border ${
                typeFilter === ""
                  ? "bg-mars border-mars text-white"
                  : "border-white/20 hover:border-ray"
              }`}
              onClick={() => setTypeFilter("")}
            >
              All types
            </button>
            {types.map((type) => (
              <button
                key={type.id}
                className={`text-xs px-3 py-1 rounded-full border ${
                  typeFilter === type.id
                    ? "bg-mars border-mars text-white"
                    : "border-alt-wht/30 text-alt-wht hover:border-ray"
                }`}
                onClick={() =>
                  setTypeFilter(type.id === typeFilter ? "" : type.id)
                }
              >
                {type.label}
              </button>
            ))}
          </div>

          <p className="text-xs text-alt-wht/60">
            {filteredEvents.length} matching reports
            {selectedFeature ? ` · ${selectedFeature.name}` : " · click a marker"}
          </p>

          {selectedFeature && (
            <div className="border-t border-white/10 pt-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-ray">{selectedFeature.name}</h2>
                  <p className="text-xs text-alt-wht/70">{selectedFeature.kind}</p>
                </div>
                <button
                  className="text-xs text-sunset hover:text-ray"
                  onClick={() => setSelectedId(null)}
                >
                  Clear
                </button>
              </div>
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {selectedEvents.map((event) => (
                  <li key={event.id}>
                    <Link
                      to={`/events/${event.id}`}
                      className="block rounded-xl bg-white/5 hover:bg-white/10 p-3 transition-colors"
                    >
                      <div className="text-xs text-ray font-semibold">
                        {formatEventDate(event)} · {TYPE_LABELS[event.phenomenonTypeId]}
                      </div>
                      <div className="text-sm mt-1 line-clamp-2">{event.description}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </Layout>
  );
};

export default MapView;
